/**
 * 설비 메타/상태 공유 캐시
 * ----------------------------------------------------------------------------
 *  - 대시보드 위젯들이 equipType 정의(equipTypeInfo)와 설비 상태 구조(currentEquipStatus)를
 *    각자 fetch 하면 동일 데이터를 중복 호출하게 된다. 여기서 한 번만 가져와 공유한다.
 *  - equipTypeInfo : equipType 단위로 캐싱 (위젯이 N개여도 타입 수만큼만 호출)
 *  - equipStatus   : (sysNodeIdx, equipType) 단위로 캐싱 + in-flight dedupe
 *  - TTL 은 서버 캐시(ttlInM=5)와 정렬. 실시간 값은 호출부에서 totalReal 로 덮어쓴다.
 *  - 반환 구조는 Setting 모달과 동일: Group → 배열, 그 외 → 첫 번째 객체.
 */
import backEndApi from "@api/backEndApi.js";

const TTL_MS = 5 * 60 * 1000; // getIncludedEquipStatus?ttlInM=5 와 동일

const equipTypeCache = new Map(); // equipType -> { ts, promise }
const equipStatusCache = new Map(); // `${sysNodeIdx}:${equipType}` -> { ts, promise }

function isFresh(entry) {
    return entry && Date.now() - entry.ts < TTL_MS;
}

/** equipType 정의(라벨·단위·인코딩 등) — equipType 별 1회만 조회 */
export function getEquipTypeInfo(equipType) {
    if (!equipType) return Promise.resolve(null);

    const hit = equipTypeCache.get(equipType);
    if (isFresh(hit)) return hit.promise;

    const promise = backEndApi.equipTypes
        .getEquipType(equipType, "Y")
        .then((res) => (res?.status === 200 ? res.data : null))
        .catch((err) => {
            equipTypeCache.delete(equipType); // 실패는 캐싱하지 않음(재시도 허용)
            throw err;
        });

    equipTypeCache.set(equipType, { ts: Date.now(), promise });
    return promise;
}

/** 설비 상태 구조 — (sysNodeIdx, equipType) 별 1회만 조회 */
export function getEquipStatus(sysNodeIdx, equipType, nodeType) {
    if (!sysNodeIdx || !equipType) return Promise.resolve(null);

    const key = `${sysNodeIdx}:${equipType}`;
    const hit = equipStatusCache.get(key);
    if (isFresh(hit)) return hit.promise;

    const promise = backEndApi.sysNode
        .getIncludedEquipStatus(sysNodeIdx, equipType)
        .then((res) => {
            const rows = res?.status === 200 ? res.data : null;
            if (!rows || rows.length === 0) return null;
            return nodeType === "Group" ? rows : rows[0];
        })
        .catch((err) => {
            equipStatusCache.delete(key);
            throw err;
        });

    equipStatusCache.set(key, { ts: Date.now(), promise });
    return promise;
}

/** 제어 직후 등 강제 갱신이 필요할 때 캐시 무효화 */
export function invalidateEquipStatus(sysNodeIdx, equipType) {
    if (sysNodeIdx && equipType) {
        equipStatusCache.delete(`${sysNodeIdx}:${equipType}`);
    } else {
        equipStatusCache.clear();
    }
}
